import { motion } from "framer-motion";
import { Clock, MessageSquare, AlertTriangle } from "lucide-react";
import { STATUS, PRIORITY, colorFor, initialsOf, hoursAgo, slaPctOf } from "@/lib/mock";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

export default function TicketCard({ ticket, onOpen, compact = false }) {
  const st = STATUS[ticket.status] || STATUS.open;
  const pr = PRIORITY[ticket.priority] || PRIORITY.medium;
  const catName = ticket.category?.name || "General";
  const catColor = colorFor(ticket.category?.id || catName);
  const tech = ticket.assignee;
  const sla = ticket.sla_pct ?? slaPctOf(ticket);
  const slaCritical = sla >= 80 && !["resolved", "closed"].includes(ticket.status);

  return (
    <motion.button
      layout
      whileHover={{ y: -3 }}
      onClick={() => onOpen?.(ticket)}
      className="w-full text-left glass rounded-2xl p-4 border-l-[3px] transition-transform hover:shadow-lg"
      style={{ borderLeftColor: st.color }}
      data-testid={`ticket-card-${ticket.ticket_no || ticket.id}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-mono text-[11px] uppercase tracking-wider text-muted-foreground">{ticket.ticket_no}</span>
        <span
          className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full"
          style={{ background: `${pr.color}18`, color: pr.color }}
        >
          {pr.label}
        </span>
      </div>
      <div className="mt-2 font-medium leading-snug line-clamp-2">{ticket.title}</div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full"
              style={{ background: `${catColor}12`, color: catColor }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: catColor }} />
          {catName}
        </span>
        {slaCritical && (
          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-600">
            <AlertTriangle className="h-3 w-3" /> SLA {sla}%
          </span>
        )}
      </div>

      {!compact && (
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {tech ? (
              <>
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-[10px] font-bold" style={{ background: colorFor(tech.id), color: "white" }}>
                    {initialsOf(tech.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs text-muted-foreground truncate max-w-[140px]">{tech.name}</span>
              </>
            ) : (
              <span className="text-xs text-muted-foreground">Unassigned</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{hoursAgo(ticket.created_at)}</span>
            {ticket.comments_count != null && (
              <span className="inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" />{ticket.comments_count}</span>
            )}
          </div>
        </div>
      )}
    </motion.button>
  );
}
